import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import styled from 'styled-components';
import { connect } from 'react-redux';
import {
  Confirm,
  Dropdown,
  Menu,
  Loader,
  Table,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableHeaderCell,
  Dimmer
} from 'semantic-ui-react';

import FilterBar, { FilterField, FilterValues } from '../../../components/Filter';
import EstateUnitFilter from '../../../components/EstateUnitFilter';
import { DateOnlyToStringDate } from '../../../services/date';
import { numberToMoneyString } from '../../../services/masks';
import InvoiceDetailModal from './Details';
import AdminEditInvoiceModal, {
  AdminEditInvoiceModalData
} from './AdminEditInvoiceModal';
import { iRootDispatch, iRootState } from '../../../store';
import { AvailableBrokersAndEstatesState } from '../../../store/types/temp-types';
import { CustoDropDownItemProps } from '../../../components/types';
import IconButton from '../../../components/IconButton';
import { InvoicesStatusList, translateInvoiceStatus } from '../utils/AlfaSeguros';
import {
  getInvoices,
  getInvoicesBillet,
  softDeleteFireInsuranceInvoice
} from '../../../services/fire-insurance-invoices';
import ProLaboreDetailModal from './ProLaboreDetails';
import { useDropDownUsers } from '../../../services/broker';
import { UsersToCustomDropDownOptions } from '../../../util';
import { UserState } from '../../../types/UserState';
import TablePaginationMenu from '../../../components/TablePaginationMenu';
import { getFiles } from '../../../services/file';
import {
  dangerNotification,
  successNotification,
  warningNotification
} from '../../../services/notification';
import { appendEstateUnitIdToFilter } from '../../../utils/estateUnitFilter.util';
import { getListEmptyMessage } from '../../../utils/estateUnitEmptyState.util';
import { isBackOfficeUser } from '../../../utils/isBackOfficeUser';
import { isEstateRole } from '../../../utils/isEstateRole';
import { UserRolesEnum } from '../../../enums/user-roles.enum';

const ClickableTableCell = styled(TableCell)`
  cursor: pointer;
  position: relative;
`;

const ClickableTableRow = styled(TableRow)`
  :hover {
    background-color: #f0f0f0;
  }
`;

type SelectOption = { value: string; text: string };

type ApiFilters = {
  insurerId?: string;
  estateId?: string;
  accountManager?: string;
  start?: string;
  end?: string;
  status?: string;
  estateUnitId?: string;
  page?: number;
  isRv?: boolean;
};

const toSelectOptions = (
  items: SelectOption[]
): Array<{ value: string; label: string }> =>
  items.map(item => ({
    value: String(item.value),
    label: String(item.text)
  }));

const formatDateValue = (value: any): string | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) {
    return moment(value).format('YYYY-MM-DD');
  }
  const asString = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(asString)) {
    return asString.substring(0, 10);
  }
  const parsed = moment(asString, ['YYYY-MM-DD', 'DD/MM/YYYY'], true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : asString;
};

const buildInitialFilterValues = (singleEstateId: string): FilterValues => ({
  estateId: singleEstateId,
  insurerId: '',
  status: '',
  start: '',
  end: '',
  estateUnitId: '',
  accountManager: ''
});

const normalizeToApiFilter = (values: FilterValues): ApiFilters => {
  const filter: ApiFilters = {};

  if (values.estateId) filter.estateId = String(values.estateId);
  if (values.insurerId) filter.insurerId = String(values.insurerId);
  if (values.status) filter.status = String(values.status);

  const start = formatDateValue(values.start);
  const end = formatDateValue(values.end);
  if (start) filter.start = start;
  if (end) filter.end = end;

  if (values.accountManager) filter.accountManager = String(values.accountManager);

  return appendEstateUnitIdToFilter(
    filter as Record<string, unknown>,
    String(values.estateUnitId || '')
  ) as ApiFilters;
};

const FireInsuranceInvoices = (props: {
  availableBrokersAndEstates: AvailableBrokersAndEstatesState;
  user: UserState;
}) => {
  const ALFA_ID = '6101f0f8-ae05-452c-94a3-861c8a8004c4';
  const YELUM_ID = '9ebc4304-201d-4cf2-9c8a-6d8cc90ec968';
  const { availableBrokersAndEstates, user } = props;
  const isBackOffice = isBackOfficeUser();
  const isEstateUser = isEstateRole(user?.role);
  const isBrokerAdmin = user?.role === UserRolesEnum.brokerAdmin;

  const estatesOptions: CustoDropDownItemProps[] = availableBrokersAndEstates.estates;
  const insurerOptions: CustoDropDownItemProps[] =
    availableBrokersAndEstates.insurers.filter(insurer =>
      insurer.value.availability.includes('FIRE_INSURANCE')
    );

  const singleEstateId = useMemo(() => {
    if (estatesOptions.length !== 1) return '';
    return String(estatesOptions[0]?.value?.id || '');
  }, [estatesOptions]);

  const initialFilterValues = useMemo(
    () => buildInitialFilterValues(singleEstateId),
    [singleEstateId]
  );

  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [isDownloadBillet, setIsDownloadBillet] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openProLaboreModal, setOpenProLaboreModal] = useState<boolean>(false);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [editInvoiceData, setEditInvoiceData] = useState<
    AdminEditInvoiceModalData | undefined
  >(undefined);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<any>(null);
  const [isDeletingInvoice, setIsDeletingInvoice] = useState<boolean>(false);
  const [currentInvoice, setCurrentInvoice] = useState<any>(null);
  const [invoicesData, setInvoicesData] = useState<any[]>([]);
  const [count, setCount] = useState<number>(0);
  const [appliedFilters, setAppliedFilters] = useState<ApiFilters>(() =>
    normalizeToApiFilter(initialFilterValues)
  );

  const appliedFiltersRef = useRef(appliedFilters);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  const { data: usersData, isLoading: isAccountManagersLoading } = useDropDownUsers(
    user?.broker?.id
  );

  const accountManagerOptions = React.useMemo(() => {
    if (!usersData) return [];
    return UsersToCustomDropDownOptions(
      false,
      usersData.map((item: any) => ({
        id: item.id,
        name: item.name
      }))
    );
  }, [usersData]);

  const selectableEstates: SelectOption[] = useMemo(
    () =>
      estatesOptions.map(item => ({
        value: String(item.value?.id || ''),
        text: String(item.text || '')
      })),
    [estatesOptions]
  );

  const selectableInsurers: SelectOption[] = useMemo(
    () =>
      insurerOptions.map(item => ({
        value: String(item.value?.id || ''),
        text: String(item.text || '')
      })),
    [insurerOptions]
  );

  const selectableAccountManagers: SelectOption[] = useMemo(
    () =>
      accountManagerOptions.map((item: any) => ({
        value: String(item.value),
        text: String(item.text)
      })),
    [accountManagerOptions]
  );

  const filterFields: FilterField[] = useMemo(() => {
    const fields: FilterField[] = [];

    if (isBackOffice) {
      fields.push({
        kind: 'select',
        name: 'estateId',
        label: 'Imobiliária',
        placeholder: 'Selecionar...',
        options: toSelectOptions(selectableEstates),
        getDisplayValue: values => {
          const option = selectableEstates.find(
            item => String(item.value) === String(values.estateId)
          );
          return option ? option.text : undefined;
        }
      });
    }

    fields.push(
      {
        kind: 'select',
        name: 'insurerId',
        label: 'Seguradora',
        placeholder: 'Selecionar...',
        options: toSelectOptions(selectableInsurers),
        getDisplayValue: values => {
          const option = selectableInsurers.find(
            item => String(item.value) === String(values.insurerId)
          );
          return option ? option.text : undefined;
        }
      },
      {
        kind: 'select',
        name: 'status',
        label: 'Status',
        placeholder: 'Selecionar...',
        options: toSelectOptions(InvoicesStatusList),
        getDisplayValue: values => {
          const option = InvoicesStatusList.find(
            item => String(item.value) === String(values.status)
          );
          return option ? option.text : undefined;
        }
      },
      {
        kind: 'dateRange',
        name: 'period',
        label: 'Período',
        startName: 'start',
        endName: 'end'
      },
      {
        kind: 'custom',
        name: 'estateUnitId',
        label: 'Unidade',
        advanced: true,
        getDisplayValue: values => {
          if (!values.estateUnitId) return undefined;
          if (values.estateUnitId === 'null') return 'Sem unidade';
          return String(values.estateUnitId);
        },
        render: ({ values, setValue }) => (
          <EstateUnitFilter
            estateId={String(values.estateId || singleEstateId || '')}
            value={values.estateUnitId || ''}
            onChange={value => setValue('estateUnitId', value)}
            upward
          />
        )
      }
    );

    if (!isEstateUser) {
      fields.push({
        kind: 'select',
        name: 'accountManager',
        label: 'Gestor de contas',
        placeholder: 'Selecionar...',
        advanced: true,
        loading: isAccountManagersLoading,
        options: toSelectOptions(selectableAccountManagers),
        getDisplayValue: values => {
          const option = selectableAccountManagers.find(
            item => String(item.value) === String(values.accountManager)
          );
          return option ? option.text : undefined;
        }
      });
    }

    return fields;
  }, [
    isBackOffice,
    isEstateUser,
    selectableEstates,
    selectableInsurers,
    selectableAccountManagers,
    isAccountManagersLoading,
    singleEstateId
  ]);

  const getData = useCallback(async () => {
    setIsLoadingData(true);

    const filter: ApiFilters = {
      ...appliedFiltersRef.current,
      page: currentPage,
      isRv: true
    };

    if (!filter.estateId && singleEstateId) {
      filter.estateId = singleEstateId;
    }

    const invoices = await getInvoices(filter);

    setInvoicesData(invoices?.data);
    setCount(invoices?.quantity);
    setIsLoadingData(false);
  }, [currentPage, singleEstateId]);

  const handleFilterSearch = (values: FilterValues) => {
    const next = normalizeToApiFilter(values);
    if (!next.estateId && singleEstateId) {
      next.estateId = singleEstateId;
    }
    setAppliedFilters(next);
    setCurrentPage(1);
  };

  const handleFilterClear = () => {
    setCurrentPage(1);
  };

  useEffect(() => {
    getData();
  }, [currentPage, appliedFilters, getData]);

  const handleDownloadBillet = async (invoice: any) => {
    setIsDownloadBillet(true);

    await getInvoicesBillet({
      estateId: invoice.estate.id,
      insurerId: invoice.insurer.id,
      contractCode: invoice.contractCode,
      emissionCode: invoice.emissionCode
    });

    setIsDownloadBillet(false);
  };

  async function handleDownloadSegimobFile(invoiceId: string, name: string) {
    setIsDownloadBillet(true);

    try {
      const data = await getFiles({
        tableName: 'fire_insurance_invoices',
        registerId: invoiceId,
        name
      });

      const url = data.urls[0].link;
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.setAttribute('download', data.urls[0].key);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      warningNotification("Arquivo não encontrado", "O Arquivo ainda não foi fornecido pela Seguradora, tente novamente mais tarde.  Se o problema persistir, entre em contato com seu Gestor de Contas.")
    } finally {
      setIsDownloadBillet(false);
    }
  }

  const handleOpenProLaboreDetailsModal = async (invoice: any) => {
    setCurrentInvoice(invoice);
    setOpenProLaboreModal(true);
  };

  const handleCloseProLaboreDetailsModal = () => {
    setCurrentInvoice(null);
    setOpenProLaboreModal(false);
  };

  const handleCloseModal = () => {
    setCurrentInvoice(null);
    setOpenModal(false);
  };

  const handleOpenEditModal = (invoice: any) => {
    setEditInvoiceData({
      id: invoice.id,
      amount: invoice.amount,
      dueDate: invoice.dueDate,
      paymentDate: invoice.paymentDate,
      status: invoice.status,
      estate: invoice.estate,
      insurer: invoice.insurer,
      contractCode: invoice.contractCode,
      emissionCode: invoice.emissionCode,
      contributionNumber: invoice.contributionNumber
    });
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setEditInvoiceData(undefined);
  };

  const handleOpenDeleteConfirm = (invoice: any) => {
    setInvoiceToDelete(invoice);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    if (isDeletingInvoice) {
      return;
    }
    setDeleteConfirmOpen(false);
    setInvoiceToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!invoiceToDelete?.id || isDeletingInvoice) {
      return;
    }

    setIsDeletingInvoice(true);
    try {
      await softDeleteFireInsuranceInvoice(invoiceToDelete.id);
      successNotification('Sucesso', 'Fatura excluída com sucesso.');
      setDeleteConfirmOpen(false);
      setInvoiceToDelete(null);
      await getData();
    } catch (error) {
      const rawMessage = (error as any)?.message;
      const message = Array.isArray(rawMessage)
        ? rawMessage.join(', ')
        : rawMessage || 'Não foi possível excluir a fatura.';
      dangerNotification('Erro', message);
    } finally {
      setIsDeletingInvoice(false);
    }
  };

  const renderDetailModal = (invoice: any) => {
    setCurrentInvoice(invoice);
    setOpenModal(true);
  };

  return (
    <>
      {isDownloadBillet ? (
        <Dimmer active inverted page>
          <Loader indeterminate>Baixando arquivo...</Loader>
        </Dimmer>
      ) : null}

      <FilterBar
        fields={filterFields}
        initialValues={initialFilterValues}
        onSearch={handleFilterSearch}
        onClear={handleFilterClear}
        loading={isLoadingData}
        syncToUrl
        urlNamespace="fire-invoices"
      />

      <Table singleLine>
        <TableHeader fullWidth>
          <TableRow>
            <TableHeaderCell textAlign={'center'}>Gerado em</TableHeaderCell>
            <TableHeaderCell textAlign={'center'}>Data de vencimento</TableHeaderCell>
            <TableHeaderCell textAlign={'center'}>Data de pagamento</TableHeaderCell>
            <TableHeaderCell textAlign={'center'}>Imobiliária</TableHeaderCell>
            <TableHeaderCell textAlign={'center'}>Seguradora</TableHeaderCell>
            <TableHeaderCell textAlign={'center'}>Valor total</TableHeaderCell>
            <TableHeaderCell textAlign={'center'}>Gestor de contas</TableHeaderCell>
            <TableHeaderCell textAlign={'center'}>Status</TableHeaderCell>
            <TableHeaderCell textAlign={'center'}>Ações</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoadingData ? (
            <TableRow>
              <TableCell textAlign={'center'} colSpan={10}>
                <Loader active inline />
              </TableCell>
            </TableRow>
          ) : (
            <>
              {invoicesData.length ? (
                invoicesData.map((invoice: any) => {
                  const [date, hour] =
                    invoice.createdAt &&
                    moment(invoice.createdAt).format('DD/MM/YYYY HH:mm').split(' ');

                  return (
                    <ClickableTableRow key={invoice.id}>
                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(invoice)}
                      >
                        {date}
                        <br />
                        {hour}
                      </ClickableTableCell>

                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(invoice)}
                      >
                        {DateOnlyToStringDate(invoice.dueDate)}
                      </ClickableTableCell>

                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(invoice)}
                      >
                        {invoice.paymentDate ? DateOnlyToStringDate(invoice.paymentDate) : '-'}
                      </ClickableTableCell>

                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(invoice)}
                      >
                        {invoice.estate.name}
                      </ClickableTableCell>

                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(invoice)}
                      >
                        {invoice.insurer.name}
                      </ClickableTableCell>

                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(invoice)}
                      >
                        {numberToMoneyString(Number(invoice.amount))}
                      </ClickableTableCell>

                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(invoice)}
                      >
                        {invoice.estate?.user?.name}
                      </ClickableTableCell>

                      <ClickableTableCell
                        textAlign={'center'}
                        onClick={() => renderDetailModal(invoice)}
                      >
                        {translateInvoiceStatus(invoice.status)}
                      </ClickableTableCell>

                      <TableCell textAlign={'center'}>
                        <Menu compact>
                          <Menu.Item>
                            <Dropdown
                              trigger={<IconButton name="ellipsis vertical" />}
                              icon={null}
                              direction="left"
                            >
                              <Dropdown.Menu style={{ zIndex: 1000 }}>
                                {isBrokerAdmin && (
                                  <Dropdown.Item
                                    icon="pencil"
                                    text={'Editar Fatura'}
                                    onClick={() => handleOpenEditModal(invoice)}
                                  />
                                )}

                                {isBrokerAdmin && (
                                  <Dropdown.Item
                                    icon="trash"
                                    text={'Excluir Fatura'}
                                    onClick={() => handleOpenDeleteConfirm(invoice)}
                                  />
                                )}

                                <Dropdown.Item
                                  icon="download"
                                  text={'Baixar boleto'}
                                  onClick={() => {
                                    if (invoice.insurerId === ALFA_ID) {
                                      handleDownloadBillet(invoice);
                                    } else {
                                      handleDownloadSegimobFile(
                                        invoice.id,
                                        invoice.insurerId === YELUM_ID ? 'BOLETO' : 'INVOICE_BILL',
                                      );
                                    }
                                  }}
                                />

                                <Dropdown.Item
                                  icon="info circle"
                                  text={'Baixar faturas fechadas'}
                                  onClick={() =>

                                    handleDownloadSegimobFile(
                                      invoice.id,
                                      'INVOICE_REPORT'
                                    )
                                  }
                                />
                              </Dropdown.Menu>
                            </Dropdown>
                          </Menu.Item>
                        </Menu>
                      </TableCell>
                    </ClickableTableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={10} textAlign={'center'}>
                    {getListEmptyMessage(props.user?.role, props.user?.estateUnitId)}
                  </TableCell>
                </TableRow>
              )}
            </>
          )}
        </TableBody>

        <Table.Footer fullWidth>
          <TableRow>
            <TableHeaderCell colSpan={10} textAlign={'right'}>
              {!isLoadingData && (
                <TablePaginationMenu
                  currentPage={currentPage}
                  dataCount={count}
                  setCurrentPage={setCurrentPage}
                />
              )}
            </TableHeaderCell>
          </TableRow>
        </Table.Footer>
      </Table>

      {openModal && (
        <InvoiceDetailModal
          open={openModal}
          onClose={handleCloseModal}
          invoice={currentInvoice}
          key={'detailPolicy'}
        />
      )}

      {openEditModal && editInvoiceData && (
        <AdminEditInvoiceModal
          open={openEditModal}
          close={handleCloseEditModal}
          data={editInvoiceData}
          onSuccess={getData}
        />
      )}

      <Confirm
        open={deleteConfirmOpen}
        header="Excluir fatura"
        content={`Tem certeza que deseja excluir esta fatura${
          invoiceToDelete?.estate?.name
            ? ` da imobiliária ${invoiceToDelete.estate.name}`
            : ''
        }?`}
        cancelButton="Cancelar"
        confirmButton={isDeletingInvoice ? 'Excluindo...' : 'Excluir'}
        onCancel={handleCloseDeleteConfirm}
        onConfirm={handleConfirmDelete}
      />

      {openProLaboreModal && (
        <ProLaboreDetailModal
          open={openProLaboreModal}
          onClose={handleCloseProLaboreDetailsModal}
          contributionNumber={currentInvoice.contributionNumber}
          key={'proLaboreDetail'}
        />
      )}
    </>
  );
};

const mapState = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateAvailableBrokersAndEstates: (availableBrokers: AvailableBrokersAndEstatesState) =>
    dispatch.availableBrokersAndEstates.updateAvailableBrokersAndEstates(availableBrokers)
});

export default connect(mapState, mapDispatch)(FireInsuranceInvoices);
